import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TbLock, TbEye, TbEyeOff, TbRefresh, TbPlugConnected, TbTrash,
  TbCircleCheck, TbAlertTriangle, TbClock, TbBuilding,
} from "react-icons/tb";
import { api } from "../../../lib/api";
import { Button, Field, Alert, EmptyState } from "../../../components";

// The shared body of a data connector panel.
//
// Atera and Microsoft 365 differ only in which credential fields they collect and what
// the help text says. Everything else — connect, test, schedule, run history, mapping
// customers onto organizations — is identical, and two copies of it would drift the way
// the two GHL screens did before the catalogue was introduced.
//
// A provider supplies `fields` (what to render and how to validate) and copy; this owns
// the lifecycle.

// The value sent back for a secret the admin did not retype. The server treats it as
// "leave the stored credential alone", which is what stops an untouched masked field
// from overwriting a working key.
const SECRET_PLACEHOLDER = "__unchanged__";

const INTERVALS = [
  { value: "", label: "Manual only" },
  { value: "60", label: "Every hour" },
  { value: "360", label: "Every 6 hours" },
  { value: "1440", label: "Daily" },
  { value: "10080", label: "Weekly" },
];

const formatWhen = (value) => {
  if (!value) return "never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "never" : date.toLocaleString();
};

const RunStatus = ({ run }) => {
  if (!run) return null;
  const tone = run.status === "completed" ? "ok" : run.status === "failed" ? "bad" : "wait";
  const Icon = tone === "ok" ? TbCircleCheck : tone === "bad" ? TbAlertTriangle : TbClock;
  return (
    <span className={`v2-conn-run v2-conn-run--${tone}`}>
      <Icon size={13} /> {run.status}
    </span>
  );
};

const ConnectorPanel = ({ isAdmin, provider, title, fields, help, permissions }) => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [reveal, setReveal] = useState({});

  const [connection, setConnection] = useState(null);
  const [runs, setRuns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [form, setForm] = useState(() =>
    // A choice field starts on its first option: an empty select would post "" on the
    // first save, and the server would reject it as an invalid region.
    fields.reduce(
      (acc, f) => ({ ...acc, [f.key]: f.options ? f.options[0].value : "" }),
      { sync_interval_minutes: "" }
    )
  );
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const loadLinks = useCallback(async (id) => {
    try {
      const d = await api.get(`s2/api/integrations/connections/${id}/org-links`);
      setCustomers(d?.customers || []);
      setOrganizations(d?.organizations || []);
    } catch {
      // The mapping table is secondary; a failure here should not blank the panel.
      setCustomers([]);
    }
  }, []);

  const loadRuns = useCallback(async (id) => {
    try {
      const d = await api.get(`s2/api/integrations/connections/${id}/runs`);
      setRuns(d?.runs || []);
    } catch {
      setRuns([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await api.get("s2/api/integrations/connections");
      const found = (d?.connections || []).find((c) => c.provider === provider) || null;
      setConnection(found);
      if (found) {
        setForm((f) => ({
          ...f,
          sync_interval_minutes: found.sync_interval_minutes
            ? String(found.sync_interval_minutes)
            : "",
          // Non-secret values come back and are editable; secrets come back only as a
          // mask, so their inputs stay empty and mean "unchanged" until typed in.
          ...fields.reduce((acc, field) => {
            if (field.secret) return acc;
            const stored = found[field.key];
            return {
              ...acc,
              [field.key]: stored || (field.options ? field.options[0].value : ""),
            };
          }, {}),
        }));
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load this integration.");
    } finally {
      setLoading(false);
    }
  }, [provider, fields]);

  useEffect(() => { load(); }, [load]);

  // ── Following a sync while it runs ──────────────────────────────────────────
  //
  // Sync now only queues a run; the worker picks it up on its next tick (every 15s) and
  // a large account takes a while after that. The panel used to fetch the run list once,
  // right after queueing, so it showed "Queued" until someone reloaded the page — and
  // the Client mapping table, which only has rows once a sync has written them, stayed
  // on "Nothing synced yet" beside a sync that had in fact completed.
  //
  // So while any run is unfinished the list is re-fetched every few seconds, and the
  // moment the last one finishes the mapping table is reloaded and the outcome reported.
  const POLL_MS = 4000;
  const activeRun = runs.some((r) => r.status === "queued" || r.status === "running");

  useEffect(() => {
    if (!connection || !activeRun) return undefined;
    const timer = setInterval(() => loadRuns(connection.id), POLL_MS);
    return () => clearInterval(timer);
  }, [connection, activeRun, loadRuns]);

  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && !activeRun && connection) {
      // The newest run is the one that just finished.
      const latest = runs[0];
      if (latest?.status === "completed") {
        // The same figure the Records column shows, so the message and the table agree.
        const n = Number(latest.objects_written ?? 0);
        flash(`Sync complete — ${n.toLocaleString()} record${n === 1 ? "" : "s"}.`);
      } else if (latest?.status === "failed") {
        setError(latest.error || "The sync failed. See Recent syncs for details.");
      }
      // Only the mapping table, not load(): reloading the connection would reset the
      // form and discard anything the admin is part-way through typing.
      loadLinks(connection.id);
    }
    wasActive.current = activeRun;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun]);

  useEffect(() => {
    if (!connection) return;
    loadRuns(connection.id);
    loadLinks(connection.id);
  }, [connection, loadRuns, loadLinks]);

  const flash = (message) => {
    setSaved(message);
    setTimeout(() => setSaved(""), 3000);
  };

  const payload = () => {
    const body = { provider, sync_interval_minutes: form.sync_interval_minutes || null };
    for (const field of fields) {
      const value = (form[field.key] || "").trim();
      // An untouched secret on an existing connection is sent as the placeholder rather
      // than as an empty string, which would read as "clear it".
      if (field.secret && !value && connection?.has_credential) {
        body[field.key] = SECRET_PLACEHOLDER;
      } else {
        body[field.key] = value;
      }
    }
    return body;
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      if (connection) {
        const d = await api.patch(`s2/api/integrations/connections/${connection.id}`, payload());
        setConnection(d.connection);
      } else {
        const d = await api.post("s2/api/integrations/connections", payload());
        setConnection(d.connection);
      }
      // Secrets are never echoed back, so clear the inputs after a save: leaving the
      // typed value on screen implies it is being displayed from the server.
      setForm((f) => ({
        ...f,
        ...fields.reduce((acc, field) => (field.secret ? { ...acc, [field.key]: "" } : acc), {}),
      }));
      flash("Saved.");
    } catch (e) {
      setError(e.message || "Could not save these settings.");
    } finally { setBusy(false); }
  };

  const test = async () => {
    if (!connection) return;
    setTesting(true); setError("");
    try {
      const d = await api.post(`s2/api/integrations/connections/${connection.id}/sync`, {
        mode: "validate",
      });
      if (d?.run?.status === "completed") {
        flash("Connection verified.");
      } else {
        setError(d?.run?.error || "The connection test did not succeed.");
      }
      await load();
      await loadRuns(connection.id);
    } catch (e) {
      setError(e.message || "Could not test the connection.");
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    if (!connection) return;
    setBusy(true); setError("");
    try {
      await api.post(`s2/api/integrations/connections/${connection.id}/sync`, { mode: "full" });
      flash("Sync queued. This page updates when it finishes.");
      await loadRuns(connection.id);
    } catch (e) {
      setError(e.message || "Could not start a sync.");
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!connection) return;
    // Everything this connection pulled goes with it, which is not obvious from a button
    // labelled Disconnect.
    if (!window.confirm(`Disconnect ${title}? Synced assets and mappings will be removed.`)) return;
    setBusy(true); setError("");
    try {
      await api.del(`s2/api/integrations/connections/${connection.id}`);
      setConnection(null);
      setRuns([]);
      setCustomers([]);
      setForm((f) => ({ ...f, ...fields.reduce((a, x) => ({ ...a, [x.key]: "" }), {}) }));
      flash("Disconnected.");
    } catch (e) {
      setError(e.message || "Could not disconnect.");
    } finally { setBusy(false); }
  };

  const mapCustomer = async (externalId, externalName, organizationId) => {
    setBusy(true); setError("");
    try {
      await api.put(`s2/api/integrations/connections/${connection.id}/org-links`, {
        links: [{
          external_customer_id: externalId,
          external_name: externalName,
          organization_id: organizationId || null,
        }],
      });
      await loadLinks(connection.id);
    } catch (e) {
      setError(e.message || "Could not save the mapping.");
    } finally { setBusy(false); }
  };

  if (loading) return <p className="v2-hint">Loading…</p>;

  // Required non-secret fields must be filled; a secret is required only when there is
  // no stored credential to fall back on.
  const ready = fields.every((field) => {
    if (!field.required) return true;
    if (field.secret && connection?.has_credential) return true;
    return Boolean((form[field.key] || "").trim());
  });

  const verified = Boolean(connection?.credential_last_validated_at);

  return (
    <div className="v2-settings-form v2-conn">
      <Alert>{error}</Alert>
      {saved && <Alert tone="success">{saved}</Alert>}

      {/* Status ---------------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-integration-head">
          <span className="v2-ghl-icon v2-ghl-icon--teal"><TbPlugConnected size={18} /></span>
          <div>
            <div className="v2-setting-name">{title}</div>
            <div className="v2-setting-desc">{help.summary}</div>
          </div>
        </div>

        <div className={`v2-ghl-status${connection && verified ? " on" : ""}`}>
          <span className="v2-ghl-dot" />
          {!connection
            ? "Not connected"
            : !verified
              // Saving credentials is not evidence they work, and saying "connected"
              // before a successful test is how a broken connector looks healthy.
              ? "Saved but not yet verified — run Test connection"
              : `Verified ${formatWhen(connection.credential_last_validated_at)}`}
        </div>

        {connection?.last_error && (
          <Alert>{`Last run failed: ${connection.last_error}`}</Alert>
        )}

        {connection && (
          <dl className="v2-conn-meta">
            <div><dt>Last sync</dt><dd>{formatWhen(connection.last_sync_at)}</dd></div>
            <div><dt>Last success</dt><dd>{formatWhen(connection.last_success_at)}</dd></div>
            <div><dt>Next scheduled</dt><dd>{formatWhen(connection.next_sync_at)}</dd></div>
          </dl>
        )}
      </section>

      {/* Credentials ----------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-setting-name">Connection</div>

        {fields.map((field) => (
          <Field
            key={field.key}
            label={<>{field.secret ? <TbLock size={13} /> : null} {field.label}{field.required ? " *" : ""}</>}
          >
            {field.secret ? (
              <div className="v2-ghl-secret">
                <input
                  className="v2-input"
                  type={reveal[field.key] ? "text" : "password"}
                  value={form[field.key]}
                  disabled={!isAdmin}
                  aria-label={field.label}
                  placeholder={connection?.has_credential ? connection.credential_hint || "••••••••" : ""}
                  onChange={(e) => set({ [field.key]: e.target.value })}
                />
                <button
                  type="button"
                  className="v2-ghl-reveal"
                  aria-label={reveal[field.key] ? `Hide ${field.label}` : `Show ${field.label}`}
                  onClick={() => setReveal((r) => ({ ...r, [field.key]: !r[field.key] }))}
                >
                  {reveal[field.key] ? <TbEyeOff size={16} /> : <TbEye size={16} />}
                </button>
              </div>
            ) : field.options ? (
              /* A fixed choice rather than free text. NinjaOne's region is the case this
                 exists for: it selects which instance the credential is sent to, and a
                 typed value would be a way to aim a stored secret at another host. */
              <select
                className="v2-input"
                value={form[field.key]}
                disabled={!isAdmin}
                aria-label={field.label}
                onChange={(e) => set({ [field.key]: e.target.value })}
              >
                {field.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                className="v2-input"
                value={form[field.key]}
                disabled={!isAdmin}
                aria-label={field.label}
                onChange={(e) => set({ [field.key]: e.target.value })}
              />
            )}
            {field.help && <p className="v2-ghl-help">{field.help}</p>}
          </Field>
        ))}

        {connection?.has_credential && (
          <p className="v2-ghl-help">
            Leave a secret field blank to keep the stored value. Secrets are encrypted and
            are never shown again after saving.
          </p>
        )}

        {permissions?.length > 0 && (
          <div className="v2-ghl-help">
            <b>Grant admin consent for exactly these Microsoft Graph application permissions:</b>
            <ul>{permissions.map((p) => <li key={p}><code>{p}</code></li>)}</ul>
          </div>
        )}

        {help.steps && (
          <div className="v2-ghl-help">
            <b>How to get these:</b>
            <ol>{help.steps.map((step, i) => <li key={i}>{step}</li>)}</ol>
          </div>
        )}
      </section>

      {/* Schedule -------------------------------------------------------- */}
      <section className="v2-integration">
        <div className="v2-setting-name">Sync schedule</div>
        <Field label="Automatic sync">
          <select
            className="v2-input"
            value={form.sync_interval_minutes}
            disabled={!isAdmin}
            aria-label="Automatic sync interval"
            onChange={(e) => set({ sync_interval_minutes: e.target.value })}
          >
            {INTERVALS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <p className="v2-ghl-help">
            Manual only means nothing is pulled until you press Sync now.
          </p>
        </Field>
      </section>

      {/* Actions --------------------------------------------------------- */}
      {isAdmin && (
        <div className="v2-formactions">
          <Button variant="primary" onClick={save} disabled={!ready || busy}>
            {connection ? "Save changes" : "Connect"}
          </Button>
          {connection && (
            <>
              <Button onClick={test} disabled={testing || busy}>
                {testing ? "Testing…" : "Test connection"}
              </Button>
              <Button onClick={syncNow} disabled={busy || !verified}>
                <TbRefresh size={15} /> Sync now
              </Button>
              <Button onClick={disconnect} disabled={busy}>
                <TbTrash size={15} /> Disconnect
              </Button>
            </>
          )}
        </div>
      )}

      {/* Organization mapping -------------------------------------------- */}
      {connection && (
        <section className="v2-integration">
          <div className="v2-setting-name"><TbBuilding size={15} /> Client mapping</div>
          <div className="v2-setting-desc">
            Which Pipsqueak organization each {help.customerNoun} belongs to. Unmapped
            {" "}{help.customerNoun}s still sync — their assets just are not attached to a
            client yet.
          </div>

          {customers.length === 0 ? (
            <EmptyState title="Nothing synced yet">
              Run a sync and the {help.customerNoun}s found in {title} will be listed here.
            </EmptyState>
          ) : (
            <table className="v2-table">
              <thead>
                <tr>
                  <th>{help.customerNoun}</th>
                  <th>Assets</th>
                  <th>Organization</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.external_customer_id}>
                    <td>{customer.external_name || customer.external_customer_id}</td>
                    <td className="v2-num">{customer.asset_count}</td>
                    <td>
                      <select
                        className="v2-input"
                        value={customer.organization_id || ""}
                        disabled={!isAdmin || busy}
                        aria-label={`Organization for ${customer.external_name || customer.external_customer_id}`}
                        onChange={(e) => mapCustomer(
                          customer.external_customer_id,
                          customer.external_name,
                          e.target.value
                        )}
                      >
                        <option value="">Not mapped</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {/* History --------------------------------------------------------- */}
      {connection && runs.length > 0 && (
        <section className="v2-integration">
          <div className="v2-setting-name">Recent syncs</div>
          <table className="v2-table">
            <thead>
              <tr><th>When</th><th>Mode</th><th>Status</th><th>Records</th></tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>{formatWhen(run.created_at)}</td>
                  <td>{run.mode}</td>
                  <td>
                    <RunStatus run={run} />
                    {run.error && <div className="v2-conn-error">{run.error}</div>}
                  </td>
                  <td className="v2-num">{run.objects_written}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
};

export default ConnectorPanel;
